import {
  ForwardedRef,
  InputHTMLAttributes,
  forwardRef,
  ReactNode,
  useRef,
  useImperativeHandle,
  useState,
} from 'react';
import {InputMask, InputMaskProps} from '@react-input/mask';

import {cn} from '@/utils';

import Label from './Label';

export type InputFieldProps = {
  label?: ReactNode | string;
  rightSlot?: ReactNode;
  width?: number | string;
  maskOption?: InputMaskProps;
  containerClassName?: string;
  bottomText?: ReactNode | null;
} & InputHTMLAttributes<HTMLInputElement>;

export const InputField = forwardRef(
  (
    {
      value: initialValue,
      onChange,
      rightSlot,
      label,
      className,
      autoComplete = 'off',
      width = '100%',
      maskOption,
      containerClassName,
      bottomText,
      ...props
    }: InputFieldProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const isControlled = initialValue !== undefined;
    const [value, setValue] = useState(initialValue);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (!isControlled) {
        setValue(newValue);
      }

      onChange?.(e);
    };

    const handleClear = () => {
      if (!isControlled) {
        setValue('');
      }

      onChange?.({
        target: {
          id: props.id,
          value: '',
          name: props.name,
        },
      } as React.ChangeEvent<HTMLInputElement>);

      if (innerRef) {
        innerRef.current?.focus();
      }
    };

    const Input = maskOption ? InputMask : 'input';
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    return (
      <div
        className={cn(
          'flex flex-col gap-[0.375rem] h-full',
          containerClassName,
        )}
        style={{
          width,
        }}>
        {typeof label === 'string' ? <Label>{label}</Label> : label}
        <div
          className={cn(
            `
            flex justify-center items-center px-4 py-3 border border-neutral-300
            w-full rounded-lg focus-within:border-primary h-full`,
            className,
          )}
          style={{
            width,
          }}>
          <Input
            ref={innerRef}
            value={isControlled ? initialValue : value}
            onChange={handleChange}
            autoComplete={autoComplete}
            className={cn(
              `
              focus:outline-none focus:placeholder:text-transparent w-full
              bg-transparent
              `,
              {'cursor-default': props.readOnly},
              {'cursor-not-allowed': props.disabled},
            )}
            disabled={props.readOnly || props.disabled}
            {...maskOption}
            {...props}
          />
          {rightSlot && <span className='whitespace-nowrap'>{rightSlot}</span>}
        </div>
        {bottomText && (
          <span className='text-[0.875rem] text-red-600'>{bottomText}</span>
        )}
      </div>
    );
  },
);

InputField.displayName = 'InputField';

const InitializeButton = ({
  onClick,
  visible,
}: {
  onClick: () => void;
  visible: boolean;
}) => {
  if (!visible) {
    return null;
  }

  return (
    <img
      onClick={onClick}
      src='/assets/images/delete-input.svg'
      alt='delete'
      className='cursor-pointer'
    />
  );
};
